import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, NgForm } from '@angular/forms';
import {
  MatLegacyDialog,
  MatLegacyDialogRef,
  MatLegacyDialogState
} from '@angular/material/legacy-dialog';
import { NavigationStart, Router, RouterEvent } from '@angular/router';
import { Item } from '@sitecore-jss/sitecore-jss/layout';
import { ComponentRendering } from '@sitecore-jss/sitecore-jss-angular';
import { Subscription } from 'rxjs/internal/Subscription';
import { filter } from 'rxjs/operators';
import {
  DialogState,
  DialogStateService,
  IDialogState
} from '../../../dialogs/services/dialog-state.service';
import { DefaultPortfolioService } from '../../../shared/services/default-portfolio.service';
import {
  FormFields,
  FormFieldsGeneratorService
} from '../../../shared/services/form-fields-generator.service';
import { IPortfolio } from './../../../shared/services/portfolio.service';

@Component({
  selector: 'ab-default-portfolio-selector',
  templateUrl: './default-portfolio-selector.component.html',
  styleUrls: ['./default-portfolio-selector.component.scss']
})
export class DefaultPortfolioSelectorComponent implements OnInit, OnDestroy {
  @Input() rendering: ComponentRendering;
  @ViewChild('defaultPortfolioSelector')
  defaultPortfolioSelector: TemplateRef<any>;
  @ViewChild('defaultPortfolioSelectorForm')
  defaultPortfolioSelectorForm: NgForm;

  public portfolios: Array<IPortfolio>;
  public accounts: any;
  public dialogRef: MatLegacyDialogRef<any>;
  public tableColumns: Item;
  public formFields: FormFields;
  public selectDefaultAccount: any;
  public isLoading: boolean = true;
  public portfolioForm = new UntypedFormGroup({
    defaultAccount: new UntypedFormControl(''),
    defaultPortfolio: new UntypedFormControl('')
  });
  private dialogId: string;
  private dialogStateSubscription: Subscription;
  private routerSubscription: Subscription;
  private accountDetailsSubscription: Subscription;
  constructor(
    private defaultPortfolioService: DefaultPortfolioService,
    private dialog: MatLegacyDialog,
    private formFieldsGeneratorService: FormFieldsGeneratorService,
    private dialogStateService: DialogStateService,
    private router: Router
  ) {}

  ngOnInit() {
    this.dialogId = (<any>this.rendering.fields._id).value;

    this.routerSubscription = this.router.events
      .pipe(filter((event: RouterEvent) => event instanceof NavigationStart))
      .subscribe(() => {
        if (this.dialogRef) {
          this.dialogRef.close();
        }
      });

    this.dialogStateSubscription = this.dialogStateService
      .getDialogState()
      .subscribe((value: IDialogState) => {
        if (
          this.dialogId === value.rendaringName &&
          value.dialogState === DialogState.Open
        ) {
          if (
            !this.dialogRef ||
            this.dialogRef?.getState() !== MatLegacyDialogState.OPEN
          ) {
            this.dialogRef = this.dialog.open(this.defaultPortfolioSelector, {
              panelClass: 'popup-dialog',
              disableClose: true
            });
          }
          if (value.data) {
            this.portfolios = value.data?.portfolios;
            this.accounts = value.data?.accounts;
            this.isLoading = value.data?.isLoading;
          }
        }
      });

    this.tableColumns = (this.rendering.fields.items as Item[]).filter(
      (i) => i.name === 'Table'
    )[0] as Item;

    this.formFields = this.formFieldsGeneratorService.generateFormFields(
      this.rendering
    ) as FormFields;
  }

  setDefault(selectedPortfolio: IPortfolio) {
    this.portfolios.forEach((portfolio) => {
      if (portfolio.id !== selectedPortfolio.id) {
        portfolio.isDefault = false;
      }
    });
  }

  getDefaultAccount(portfolio: IPortfolio) {
    const accountOptions = this.accounts?.filter((account) => {
      return account.portfolioId === portfolio.id;
    });
    const defaultSelectedAccount = accountOptions?.filter(
      (account) => account.isDefaultAccount
    )[0];
    const originalDefaultAccounts = accountOptions?.filter((account) => {
      account.accountId === portfolio.defaultAccount;
    })[0];
    return defaultSelectedAccount || originalDefaultAccounts;
  }

  selectedAccountChange(event): void {
    this.portfolios.forEach((portfolio) => {
      if (portfolio.id === event.value.portfolioId) {
        portfolio.defaultAccount = event.value.accountId;
      }
    });
  }

  onClose() {
    this.dialogRef.close(false);
    this.dialogStateService.closeDialogByReference(this.dialogId, false);
  }

  submit() {
    this.defaultPortfolioService.clearDefaultPortfolio();
    const updatedPortfolios = this.portfolios.map((portfolio) => {
      return {
        portfolioName: portfolio.name,
        portfolioNumber: portfolio.id,
        isDefaultPortfolio: portfolio.isDefault,
        defaultAccount: portfolio?.defaultAccount
      };
    });

    this.defaultPortfolioService
      .setPortfolio(updatedPortfolios)
      .subscribe(() => {
        this.dialogStateService.closeDialogByReference(this.dialogId, true);
        this.dialogRef.close(true);
      });
  }

  ngOnDestroy() {
    if (this.dialogStateSubscription) {
      this.dialogStateSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.accountDetailsSubscription) {
      this.accountDetailsSubscription.unsubscribe();
    }
    this.accounts = [];
  }
}
